/**
 * Macro #27 — Onboarding V1 frontend API client.
 *
 * The wallet is derived server-side from the SIWE session (never sent). The
 * client may set persona / current_step / dismissed / selected_territory only —
 * `completed` is SERVER-DERIVED. Writes reuse the exact hardened authority
 * (SIWE session cookie + memory-only session-bound CSRF shared with
 * territory-identity-api + browser Origin).
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class OnboardingApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "OnboardingApiError";
    this.status = status;
    this.code = code;
  }
}

export type OnboardingPersona = "creator" | "retail";

export interface OnboardingGuidance {
  version: number;
  wallet: string;
  persona: OnboardingPersona | null;
  current_step: number;
  selected_territory: string | null;
  dismissed: boolean;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingCanonicalTerritory {
  identity_id: string;
  owned: boolean;
  draft_revision: number;
  has_active_publication: boolean;
  publication_state: "UNPUBLISHED" | "PUBLISHED_CURRENT" | "DRAFT_CHANGED" | "TERRITORY_CHANGED";
  publishable: boolean;
  can_publish: boolean;
  publish_blocked_reason: string | null;
}

export interface OnboardingCanonical {
  selected_territory: OnboardingCanonicalTerritory | null;
  ownership_lost?: boolean;
}

export interface OnboardingResponse {
  onboarding: OnboardingGuidance;
  canonical: OnboardingCanonical;
}

export interface OnboardingPatch {
  persona?: OnboardingPersona | null;
  current_step?: number;
  dismissed?: boolean;
  selected_territory?: string | null;
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    throw new OnboardingApiError(
      resp.status,
      env.code ?? env.detail?.code ?? "ONBOARDING_ERROR",
      env.message ?? env.detail?.message ?? resp.statusText,
    );
  }
  return body;
}

export async function getOnboardingState(signal?: AbortSignal): Promise<OnboardingResponse> {
  const resp = await fetch(`${API_BASE}/api/v1/onboarding/state`, {
    credentials: "include",
    cache: "no-store",
    signal,
  });
  return (await parse(resp)) as OnboardingResponse;
}

async function csrfSend(method: "PATCH" | "POST", path: string, body: string, signal?: AbortSignal): Promise<Response> {
  const doSend = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body,
      signal,
    });
  let resp = await doSend(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doSend(await getCsrfToken(true));
    }
  }
  return resp;
}

export async function patchOnboardingState(patch: OnboardingPatch, signal?: AbortSignal): Promise<OnboardingResponse> {
  const resp = await csrfSend("PATCH", "/api/v1/onboarding/state", JSON.stringify(patch), signal);
  return (await parse(resp)) as OnboardingResponse;
}

export async function restartOnboarding(signal?: AbortSignal): Promise<OnboardingResponse> {
  const resp = await csrfSend("POST", "/api/v1/onboarding/restart", JSON.stringify({}), signal);
  return (await parse(resp)) as OnboardingResponse;
}
